import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { IDP_CLASS_OPTIONS } from './IdpClassesMultiselect';

const EMPTY_STAGE_MS = {
  validation: 0,
  preprocessing: 0,
  ocr: 0,
  chunking: 0,
  llmExtraction: 0,
  llmConsolidation: 0,
  normalization: 0,
};

function sumStagesMs(st) {
  return (
    st.validation
    + st.preprocessing
    + st.ocr
    + st.chunking
    + st.llmExtraction
    + st.llmConsolidation
    + st.normalization
  );
}

function inferIndexBase(values) {
  if (values.length === 0) return 0;
  const min = Math.min(...values);
  if (min <= 0) return 0;
  if (min === 1) return 1;
  return min;
}

function buildTableGrid(t) {
  const rowBase = inferIndexBase(t.cells.map((c) => c.row));
  const colBase = inferIndexBase(t.cells.map((c) => c.col));
  const map = new Map();
  let inferredMaxRow = -1;
  let inferredMaxCol = -1;

  for (const cell of t.cells) {
    const r = cell.row - rowBase;
    const c = cell.col - colBase;
    if (r < 0 || c < 0) continue;
    inferredMaxRow = Math.max(inferredMaxRow, r);
    inferredMaxCol = Math.max(inferredMaxCol, c);
    map.set(`${r}:${c}`, cell.text ?? '');
  }

  const inferredRows = inferredMaxRow >= 0 ? inferredMaxRow + 1 : 0;
  const inferredCols = inferredMaxCol >= 0 ? inferredMaxCol + 1 : 0;
  return {
    cellLookup: map,
    rowCount: Math.max(t.rows, inferredRows),
    colCount: Math.max(t.cols, inferredCols),
  };
}

function humanizeClassLabel(value, t) {
  const match = IDP_CLASS_OPTIONS.find((opt) => opt.value === value);
  if (match) return t(match.labelKey);
  return String(value || '').replaceAll('_', ' ');
}

function formatConfidence(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  if (value <= 1) return `${Math.round(value * 100)}%`;
  return String(value);
}

function SectionBlock({ title, help, children }) {
  return (
    <section className="idp-result-block">
      <h3 className="idp-result-block__title">{title}</h3>
      {help ? <p className="idp-field-hint idp-field-hint--compact">{help}</p> : null}
      {children}
    </section>
  );
}

SectionBlock.propTypes = {
  title: PropTypes.string.isRequired,
  help: PropTypes.string,
  children: PropTypes.node.isRequired,
};

SectionBlock.defaultProps = {
  help: null,
};

function MetaLine({ label, value }) {
  return (
    <p className="idp-result-meta__line">
      <strong>{label}</strong>
      {' '}
      <span>{value ?? '—'}</span>
    </p>
  );
}

MetaLine.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

MetaLine.defaultProps = {
  value: null,
};

function IdpResultPanel({
  result,
  formSnapshot,
  phase,
  onExportJson,
  onChangeFile,
}) {
  const { t } = useTranslation();

  if (!result) return null;

  const stageMs = result.metadata?.stageTimings ?? EMPTY_STAGE_MS;
  const stagesSum = sumStagesMs(stageMs);
  const processingTimeMs = result.metadata?.processingTimeMs ?? 0;
  const remainderMs = Math.max(0, processingTimeMs - stagesSum);
  const stageRows = [
    { key: 'validation', title: t('idpPage.result.stageRows.validation') },
    { key: 'preprocessing', title: t('idpPage.result.stageRows.preprocessing') },
    { key: 'ocr', title: t('idpPage.result.stageRows.ocr') },
    { key: 'chunking', title: t('idpPage.result.stageRows.chunking') },
    { key: 'llmExtraction', title: t('idpPage.result.stageRows.llmExtraction') },
    { key: 'llmConsolidation', title: t('idpPage.result.stageRows.llmConsolidation') },
    { key: 'normalization', title: t('idpPage.result.stageRows.normalization') },
  ];

  const preprocessLabel = (() => {
    if (!formSnapshot) return '—';
    if (formSnapshot.skipPreprocessing) return t('idpPage.result.skipped');
    const profile = formSnapshot.preprocessProfile;
    if (profile === 'handwriting') return t('idpPage.form.preprocessProfileHandwriting');
    if (profile === 'print') return t('idpPage.form.preprocessProfilePrint');
    return t('idpPage.form.preprocessProfileDefault');
  })();

  const classesLabel = formSnapshot?.expectedClasses?.length
    ? formSnapshot.expectedClasses.map((c) => humanizeClassLabel(c, t)).join(', ')
    : '—';

  const hasErrors = (result.errors ?? []).length > 0;

  return (
    <div className="idp-result-panel idp-card">
      <div className="idp-result-panel__header">
        <h2 className="idp-result-panel__title">{t('idpPage.result.title')}</h2>
        <div className="idp-result-panel__actions">
          <button type="button" className="idp-btn idp-btn--outline" onClick={onChangeFile}>
            {t('idpPage.result.changeFile')}
          </button>
          <button type="button" className="idp-btn idp-btn--outline" onClick={onExportJson}>
            {t('idpPage.result.exportJson')}
          </button>
        </div>
      </div>

      {hasErrors || phase === 'failed' ? (
        <div className="idp-result-panel__errors-banner">
          {(result.errors ?? []).map((e, i) => (
            <p key={i} className="idp-result-panel__errors-line">
              [
              {e.stage}
              ]
              {' '}
              {e.code}
              :
              {' '}
              {e.message}
            </p>
          ))}
        </div>
      ) : null}

      {formSnapshot ? (
        <SectionBlock
          title={t('idpPage.result.chosenSettings')}
          help={t('idpPage.result.chosenSettingsHelp')}
        >
          <div className="idp-result-meta">
            <MetaLine label={`${t('idpPage.result.metaFile')}:`} value={formSnapshot.fileName} />
            <MetaLine label={`${t('idpPage.result.metaClasses')}:`} value={classesLabel} />
            <MetaLine
              label={`${t('idpPage.result.metaThreshold')}:`}
              value={formSnapshot.threshold != null ? `${Math.round(formSnapshot.threshold * 100)}%` : '—'}
            />
            <MetaLine label={`${t('idpPage.result.metaLocale')}:`} value={formSnapshot.locale} />
            <MetaLine label={`${t('idpPage.result.metaPreprocess')}:`} value={preprocessLabel} />
            <MetaLine
              label={`${t('idpPage.result.metaSkipPreprocess')}:`}
              value={formSnapshot.skipPreprocessing ? t('idpPage.result.yes') : t('idpPage.result.no')}
            />
            <MetaLine
              label={`${t('idpPage.result.metaHandwritingHints')}:`}
              value={formSnapshot.markHandwritingHints ? t('idpPage.result.yes') : t('idpPage.result.no')}
            />
          </div>
        </SectionBlock>
      ) : null}

      <SectionBlock
        title={t('idpPage.result.documentSummary')}
        help={t('idpPage.result.documentSummaryHelp')}
      >
        <div className="idp-result-highlight">
          {result.documentSummary?.trim()
            ? result.documentSummary.trim()
            : t('idpPage.result.noDocumentSummary')}
        </div>
      </SectionBlock>

      <SectionBlock
        title={t('idpPage.result.classification')}
        help={t('idpPage.result.classificationHelp')}
      >
        <MetaLine
          label={`${t('idpPage.result.predicted')}:`}
          value={result.classification?.predictedClass ?? '—'}
        />
        <MetaLine
          label={`${t('idpPage.result.confidence')}:`}
          value={formatConfidence(result.classification?.confidence)}
        />
        <MetaLine
          label={`${t('idpPage.result.reviewRequired')}:`}
          value={result.classification?.reviewRequired
            ? t('idpPage.result.yes')
            : t('idpPage.result.no')}
        />
        {(result.classification?.alternatives ?? []).length > 0 ? (
          <ul className="idp-result-list">
            {result.classification.alternatives.map((a, idx) => (
              <li key={`${a.label}-${idx}`}>
                {a.label}
                :
                {' '}
                {formatConfidence(a.confidence)}
              </li>
            ))}
          </ul>
        ) : null}
      </SectionBlock>

      <SectionBlock title={t('idpPage.result.ocr')} help={t('idpPage.result.ocrHelp')}>
        <details open className="idp-result-panel__details">
          <summary className="idp-result-panel__summary">{t('idpPage.result.fullText')}</summary>
          <pre className="idp-pre-block idp-pre-block--ocr">
            {result.ocr?.fullText || t('idpPage.result.emptyText')}
          </pre>
        </details>
        <h4 className="idp-result-block__subtitle">{t('idpPage.result.pages')}</h4>
        <p className="idp-field-hint idp-field-hint--compact">{t('idpPage.result.pagesHelp')}</p>
        {(result.ocr?.pages ?? []).length === 0 ? (
          <p className="idp-muted">{t('idpPage.result.emptyText')}</p>
        ) : (
          (result.ocr?.pages ?? []).map((p) => (
            <details key={p.pageNumber} className="idp-result-panel__details">
              <summary className="idp-result-panel__summary">
                {t('idpPage.result.page')}
                {' '}
                {p.pageNumber}
                {p.hasHandwritingEvidence ? ` ${t('idpPage.result.handwritingHint')}` : ''}
              </summary>
              <ul className="idp-tree">
                {p.lines.map((line, idx) => (
                  <li key={idx}>
                    <span>{line.content}</span>
                  </li>
                ))}
              </ul>
            </details>
          ))
        )}
      </SectionBlock>

      <SectionBlock
        title={t('idpPage.result.extraction')}
        help={t('idpPage.result.extractionHelp')}
      >
        <h4 className="idp-result-block__subtitle">{t('idpPage.result.fields')}</h4>
        <p className="idp-field-hint idp-field-hint--compact">{t('idpPage.result.fieldsHelp')}</p>
        {(result.formExtraction?.fields ?? []).length === 0 ? (
          <p className="idp-muted">{t('idpPage.result.noFields')}</p>
        ) : (
          <div className="idp-table-surface">
            <table className="idp-data-table">
              <thead>
                <tr>
                  <th>{t('idpPage.result.tableKey')}</th>
                  <th>{t('idpPage.result.tableValue')}</th>
                  <th>{t('idpPage.result.tableConfidence')}</th>
                </tr>
              </thead>
              <tbody>
                {result.formExtraction.fields.map((f, idx) => (
                  <tr key={`${f.key}-${idx}`}>
                    <td>{f.key}</td>
                    <td>{f.value ?? ''}</td>
                    <td>{f.confidence?.toFixed(3) ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <h4 className="idp-result-block__subtitle">{t('idpPage.result.tables')}</h4>
        <p className="idp-field-hint idp-field-hint--compact">{t('idpPage.result.tablesHelp')}</p>
        {(result.formExtraction?.tables ?? []).length === 0 ? (
          <p className="idp-muted">{t('idpPage.result.noTables')}</p>
        ) : (
          result.formExtraction.tables.map((tbl, ti) => {
            const { cellLookup, rowCount, colCount } = buildTableGrid(tbl);
            return (
              <div key={ti} className="idp-table-wrap">
                <p className="idp-muted">
                  {t('idpPage.result.tableTitle')}
                  {' '}
                  {ti + 1}
                  {' '}
                  (
                  {rowCount}
                  ×
                  {colCount}
                  )
                </p>
                <div className="idp-table-surface">
                  <table className="idp-data-table">
                    <tbody>
                      {Array.from({ length: rowCount }).map((_, r) => (
                        <tr key={r}>
                          {Array.from({ length: colCount }).map((_, c) => (
                            <td key={c}>{cellLookup.get(`${r}:${c}`) ?? ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })
        )}
      </SectionBlock>

      <SectionBlock title={t('idpPage.result.warnings')} help={t('idpPage.result.warningsHelp')}>
        {(result.warnings ?? []).length === 0 ? (
          <p className="idp-muted">{t('idpPage.result.none')}</p>
        ) : (
          <ul className="idp-result-list">
            {result.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        )}
      </SectionBlock>

      <SectionBlock title={t('idpPage.result.errors')} help={t('idpPage.result.errorsHelp')}>
        {(result.errors ?? []).length === 0 ? (
          <p className="idp-muted">{t('idpPage.result.none')}</p>
        ) : (
          <ul className="idp-result-list">
            {result.errors.map((e, i) => (
              <li key={i}>
                [
                {e.stage}
                ]
                {' '}
                {e.code}
                :
                {' '}
                {e.message}
              </li>
            ))}
          </ul>
        )}
      </SectionBlock>

      <SectionBlock title={t('idpPage.result.metadata')} help={t('idpPage.result.metadataHelp')}>
        <MetaLine
          label={`${t('idpPage.result.correlationId')}:`}
          value={result.metadata?.correlationId}
        />
        <MetaLine
          label={`${t('idpPage.result.processingTime')}:`}
          value={`${processingTimeMs} ${t('idpPage.result.ms')}`}
        />
        <h4 className="idp-result-block__subtitle">{t('idpPage.result.stageTimingsTitle')}</h4>
        <p className="idp-field-hint idp-field-hint--compact">{t('idpPage.result.stageTimingsHelp')}</p>
        <div className="idp-stage-rows">
          <div className="idp-stage-row idp-stage-row--header">
            <span>{t('idpPage.result.timingColumnStage')}</span>
            <span>{t('idpPage.result.timingColumnMs')}</span>
          </div>
          {stageRows.map(({ key, title }) => (
            <div key={key} className="idp-stage-row">
              <span>{title}</span>
              <span>{stageMs[key]}</span>
            </div>
          ))}
          <div className="idp-stage-row idp-stage-row--subtotal">
            <strong>{t('idpPage.result.stageTimingsSum')}</strong>
            <strong>{stagesSum}</strong>
          </div>
          {remainderMs > 1 ? (
            <div className="idp-stage-row idp-stage-row--remainder">
              <span>{t('idpPage.result.stageTimingsRemainder')}</span>
              <span>{remainderMs}</span>
            </div>
          ) : null}
        </div>
      </SectionBlock>

      <SectionBlock title="modelInfo" help={t('idpPage.result.modelInfoHelp')}>
        <pre className="idp-pre-block idp-pre-block--small idp-pre-block--ocr">
          {JSON.stringify(result.metadata?.modelInfo ?? {}, null, 2)}
        </pre>
      </SectionBlock>
    </div>
  );
}

IdpResultPanel.propTypes = {
  result: PropTypes.object,
  formSnapshot: PropTypes.shape({
    fileName: PropTypes.string,
    expectedClasses: PropTypes.arrayOf(PropTypes.string),
    threshold: PropTypes.number,
    locale: PropTypes.string,
    preprocessProfile: PropTypes.string,
    skipPreprocessing: PropTypes.bool,
    markHandwritingHints: PropTypes.bool,
  }),
  phase: PropTypes.string,
  onExportJson: PropTypes.func.isRequired,
  onChangeFile: PropTypes.func.isRequired,
};

IdpResultPanel.defaultProps = {
  result: null,
  formSnapshot: null,
  phase: 'idle',
};

export default IdpResultPanel;
